import React from 'react';
import { useReportUpdates, ReportUpdate } from '../hooks/useReportUpdates';

interface ReportUpdatesProps {
  reportId: string;
}

const ReportUpdates: React.FC<ReportUpdatesProps> = ({ reportId }) => {
  const { updates, isLoading, error } = useReportUpdates(reportId);

  if (isLoading) {
    return (
      <div className="bg-teddy border border-urban-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-urban mb-4">Updates</h3>
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-cardboard"></div>
          <p className="mt-2 text-urban-300">Loading updates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-teddy border border-urban-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-urban mb-4">Updates</h3>
        <div className="bg-rust-50 border border-rust-200 rounded-md p-4">
          <p className="text-rust-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 shadow-card">
      <h3 className="text-2xl font-bold text-urban mb-6">
        Community Updates {updates.length > 0 && `(${updates.length})`}
      </h3>
      
      {updates.length === 0 ? (
        <div className="text-center py-12 text-urban-400">
          <div className="w-16 h-16 mx-auto mb-4 bg-urban-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-urban-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-urban mb-2">No updates yet</h4>
          <p className="text-urban-400 max-w-sm mx-auto">Be the first to share an update about this treasure! Help the community stay informed.</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-urban-200"></div>
          
          <div className="space-y-6">
            {updates.map((update, index) => (
              <UpdateItem key={update.id} update={update} isLast={index === updates.length - 1} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface UpdateItemProps {
  update: ReportUpdate;
  isLast?: boolean;
}

const UpdateItem: React.FC<UpdateItemProps> = ({ update, isLast = false }) => {
  const formatRelativeTime = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return 'Invalid Date';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className="relative flex gap-4">
      {/* Timeline Node */}
      <div className="relative flex-shrink-0">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 relative ${
          update.flagged ? 'bg-rust text-white' : 'bg-cardboard text-white'
        }`}>
          <span className="text-sm font-semibold">
            {update.userId.slice(0, 2).toUpperCase()}
          </span>
        </div>
        {/* Extend timeline line if not last */}
        {!isLast && (
          <div className="absolute top-12 left-1/2 transform -translate-x-1/2 w-0.5 h-6 bg-urban-200"></div>
        )}
      </div>

      {/* Update Content */}
      <div className={`flex-1 pb-6 ${isLast ? '' : 'border-b border-urban-100'}`}>
        <div className={`rounded-2xl p-6 ${
          update.flagged ? 'bg-rust-50 border border-rust-200' : 'bg-sky-neutral border border-urban-100'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-urban">Community Member</h4>
              <p className="text-sm text-urban-400">{formatRelativeTime(update.createdAt)}</p>
            </div>
            
            {update.flagged && (
              <div className="flex items-center gap-1 text-rust bg-white px-2 py-1 rounded-full">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-xs font-medium">Flagged</span>
              </div>
            )}
          </div>

          {/* Comment */}
          {update.comment && (
            <div className="mb-4">
              <p className="text-urban leading-relaxed whitespace-pre-wrap">{update.comment}</p>
            </div>
          )}

          {/* Tags */}
          {update.tags && update.tags.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {update.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex px-3 py-1 text-xs bg-verge-100 text-verge-800 rounded-xl font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Images */}
          {update.images && update.images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {update.images.map((image, index) => (
                <div key={index} className="aspect-square rounded-xl overflow-hidden group cursor-pointer">
                  <img
                    src={image.url}
                    alt={image.altText || `Update image ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Flagged Notice */}
          {update.flagged && update.flagReason && (
            <div className="mt-4 p-3 bg-white border border-rust-200 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-rust flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-rust-800">
                  <span className="font-medium">Flagged:</span> {update.flagReason}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportUpdates;